import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state';
import { API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_CAMPAIGNID_RESPONSES_RESULTS_STATS_PATH_KEY } from '@/swagger/constants/surveys-keys';
import {
  API_GET_SURVEY_CAMPAIGN_BY_ID_RESPONSES_RESULTS_STATS,
  KEY_GET_SURVEY_CAMPAIGN_BY_ID_RESPONSES_RESULTS_STATS,
} from '../constats';
import { MOCK_RESPONSE_GET_SURVEY_CAMPAIGN_BY_ID_RESPONSES_RESULTS_STATS } from '../mocks';

type RequestPathGetSurveyCampaignByIdResponsesResultsStats = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_CAMPAIGNID_RESPONSES_RESULTS_STATS_PATH_KEY
>['request']['path'];

type RequestQueryGetSurveyCampaignByIdResponsesResultsStats = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_CAMPAIGNID_RESPONSES_RESULTS_STATS_PATH_KEY
>['request']['query'];

type ResponseGetSurveyCampaignByIdResponsesResultsStats = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_CAMPAIGNID_RESPONSES_RESULTS_STATS_PATH_KEY
>['response']['body'];

type CallParamsGetSurveyCampaignByIdResponsesResultsStats = Omit<
  RequestPathGetSurveyCampaignByIdResponsesResultsStats,
  'organizationId'
> &
  RequestQueryGetSurveyCampaignByIdResponsesResultsStats & {
    successSaga?: CallbackSaga;
    errorSaga?: CallbackSaga;
  };

export type CallResponseGetSurveyCampaignByIdResponsesResultsStats =
  ResponseGetSurveyCampaignByIdResponsesResultsStats;

type CallErrorGetSurveyCampaignByIdResponsesResultsStats = GenericApiError;

type MetricsScoreOrganization = NonNullable<
  NonNullable<
    CallResponseGetSurveyCampaignByIdResponsesResultsStats['organizationStats']
  >['metricsScore']
>;

type MetricsScoreDepartment = NonNullable<
  NonNullable<
    CallResponseGetSurveyCampaignByIdResponsesResultsStats['departmentsStats']
  >[0]['metricsScore']
>;

type CallSelectorsGetSurveyCampaignByIdResponsesResultsStats = {
  organizationStats: CallResponseGetSurveyCampaignByIdResponsesResultsStats['organizationStats'];
  proElementsOrganizationStats: MetricsScoreOrganization;
  conElementsOrganizationStats: MetricsScoreOrganization;
  departmentsStats: CallResponseGetSurveyCampaignByIdResponsesResultsStats['departmentsStats'];
  departmentsStatsWithMetrics: CallResponseGetSurveyCampaignByIdResponsesResultsStats['departmentsStats'];
  proElementsDepartmentStats: MetricsScoreDepartment;
  conElementsDepartmentStats: MetricsScoreDepartment;
  teamStats: NonNullable<CallResponseGetSurveyCampaignByIdResponsesResultsStats['teamStats']>;
  proElementsTeamStats: MetricsScoreDepartment;
  conElementsTeamStats: MetricsScoreDepartment;
};

export const getSurveyCampaignByIdResponsesResultsStatsCall = createGeneralCall<
  CallParamsGetSurveyCampaignByIdResponsesResultsStats,
  CallResponseGetSurveyCampaignByIdResponsesResultsStats,
  CallErrorGetSurveyCampaignByIdResponsesResultsStats,
  CallSelectorsGetSurveyCampaignByIdResponsesResultsStats
>({
  id: KEY_GET_SURVEY_CAMPAIGN_BY_ID_RESPONSES_RESULTS_STATS,
  method: 'get',
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_SURVEY_CAMPAIGN_BY_ID_RESPONSES_RESULTS_STATS(
        org,
        payload.campaignId,
        payload.departmentId,
        payload.teamId,
      );
    },
  paramsSelector:
    ({ payload }) =>
    () =>
      payload,
  successMock: IS_MOCK_ENABLED({ avoidMockOnDemoMode: true })
    ? MOCK_RESPONSE_GET_SURVEY_CAMPAIGN_BY_ID_RESPONSES_RESULTS_STATS
    : undefined,
  selectors: {
    organizationStats: ({ selectors }) =>
      createSelector(selectors.response, res => res.organizationStats),
    proElementsOrganizationStats: ({ selectors }) =>
      createSelector(selectors.response, res => {
        const stats = res.organizationStats?.metricsScore || [];
        if (stats.length === 0) return [];
        const maxScore = Math.max(...stats.map(item => item.score ?? 0));
        return stats.filter(item => (item.score ?? 0) === maxScore);
      }),
    conElementsOrganizationStats: ({ selectors }) =>
      createSelector(selectors.response, res => {
        const stats = res.organizationStats?.metricsScore || [];
        if (stats.length === 0) return [];
        const minScore = Math.min(...stats.map(item => item.score ?? 0));
        return stats.filter(item => (item.score ?? 0) === minScore);
      }),
    departmentsStats: ({ selectors }) =>
      createSelector(selectors.response, res => res.departmentsStats),
    departmentsStatsWithMetrics: ({ selectors }) =>
      createSelector(
        selectors.response,
        res =>
          res.departmentsStats?.filter(ds => ds.metricsScore && ds.metricsScore?.length > 0) || [],
      ),
    proElementsDepartmentStats: ({ selectors }) =>
      createSelector(selectors.response, res => {
        const stats = res.departmentsStats?.[0]?.metricsScore || [];
        if (stats.length === 0) return [];
        const maxScore = Math.max(...stats.map(item => item.score ?? 0));
        return stats.filter(item => (item.score ?? 0) === maxScore);
      }),
    conElementsDepartmentStats: ({ selectors }) =>
      createSelector(selectors.response, res => {
        const stats = res.departmentsStats?.[0]?.metricsScore || [];
        if (stats.length === 0) return [];
        const minScore = Math.min(...stats.map(item => item.score ?? 0));
        return stats.filter(item => (item.score ?? 0) === minScore);
      }),
    teamStats: ({ selectors }) => createSelector(selectors.response, res => res.teamStats || {}),
    proElementsTeamStats: ({ selectors }) =>
      createSelector(selectors.response, res => {
        const stats = res.teamStats?.metricsScore || [];
        if (stats.length === 0) return [];
        const maxScore = Math.max(...stats.map(item => item.score ?? 0));
        return stats.filter(item => (item.score ?? 0) === maxScore);
      }),
    conElementsTeamStats: ({ selectors }) =>
      createSelector(selectors.response, res => {
        const stats = res.teamStats?.metricsScore || [];
        if (stats.length === 0) return [];
        const minScore = Math.min(...stats.map(item => item.score ?? 0));
        return stats.filter(item => (item.score ?? 0) === minScore);
      }),
  },
});
